import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUser, handleAuthCallback, login, logout, onAuthChange } from '@netlify/identity';

const AuthContext = createContext(null);

const DEV_AUTH_BYPASS =
  process.env.NODE_ENV === 'development' &&
  (process.env.REACT_APP_DEV_BYPASS_AUTH === 'true' ||
    (typeof window !== 'undefined' && ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname)));

function getDevUser(email) {
  return {
    email: email || process.env.REACT_APP_DEV_BYPASS_EMAIL || 'dev@local',
    id: 'dev-user',
    role: 'developer',
  };
}

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [callbackMessage, setCallbackMessage] = useState('');

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        if (DEV_AUTH_BYPASS) {
          if (mounted) {
            setUser(getDevUser());
            setCallbackMessage('Development auth bypass is enabled.');
            setLoading(false);
          }
          return;
        }

        const result = await handleAuthCallback();

        if (result?.type === 'invite') {
          setCallbackMessage('Invitation accepted. Finish setting up your account, then continue into the studio.');
        } else if (result?.type === 'recovery') {
          setCallbackMessage('Password recovery link processed. Please set your new password.');
        } else if (result?.type === 'confirmation') {
          setCallbackMessage('Email confirmed. You are now signed in.');
        }

        if (window.location.hash) {
          window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
        }

        const currentUser = await getUser();
        if (mounted) {
          setUser(currentUser);
          setLoading(false);
          if (result && currentUser && window.location.pathname !== '/print-studio') {
            navigate('/print-studio', { replace: true });
          }
        }
      } catch (error) {
        if (mounted) {
          setLoading(false);
          setCallbackMessage(error?.message || 'Could not complete sign-in.');
        }
      }
    };

    init();

    if (DEV_AUTH_BYPASS) {
      return () => {
        mounted = false;
      };
    }

    const unsubscribe = onAuthChange((_event, currentUser) => {
      setUser(currentUser);
    });

    return () => {
      mounted = false;
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [navigate]);

  const value = useMemo(
    () => ({
      user,
      loading,
      callbackMessage,
      isAuthenticated: Boolean(user),
      isDevAuthBypass: DEV_AUTH_BYPASS,
      async signIn(email, password) {
        if (DEV_AUTH_BYPASS) {
          const devUser = getDevUser(email?.trim());
          setUser(devUser);
          setCallbackMessage('Development auth bypass is enabled.');
          return devUser;
        }

        const currentUser = await login(email, password);
        setUser(currentUser);
        return currentUser;
      },
      async signOut() {
        if (DEV_AUTH_BYPASS) {
          setUser(null);
          return;
        }

        await logout();
        setUser(null);
      },
    }),
    [user, loading, callbackMessage]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
